import { useLocation } from 'react-router-dom'
import { usePortalClient } from './usePortalClient'

const ICON_PATHS = {
  home: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
  results: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  m365: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  plan: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
  policies: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  security: 'M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2',
  vault: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z',
  activity: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  intake: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4'
}

export function usePortalNavigation() {
  const { client, clientId } = usePortalClient()
  const location = useLocation()

  const status = client?.status || 'discovery'
  const isRetainer = status === 'retainer'
  const portalSettings = client?.portalSettings || client?.portal_settings || {}
  const completedAssessments = (client?.assessments || []).filter(a => a.status === 'completed')
  const hasCompletedAssessment = completedAssessments.length > 0

  // Build base path — use clientId in URL if present (admin preview), otherwise bare /portal
  const basePath = clientId && location.pathname.includes(clientId)
    ? `/portal/${clientId}`
    : '/portal'

  const tabs = []

  // Home — always visible
  tabs.push({
    id: 'home',
    label: 'Home',
    path: basePath,
    iconPath: ICON_PATHS.home
  })

  // Organization Profile (Intake) — always visible
  if (portalSettings.showIntake !== false) {
    tabs.push({
      id: 'intake',
      label: 'Organization Profile',
      path: `${basePath}/intake`,
      iconPath: ICON_PATHS.intake
    })
  }

  // Assessment Results — only when completed + enabled
  if (portalSettings.showResults && hasCompletedAssessment) {
    tabs.push({
      id: 'results',
      label: 'Assessment Results',
      path: `${basePath}/results`,
      iconPath: ICON_PATHS.results
    })
  }

  // M365 Report — when enabled
  if (portalSettings.showMaesterReport) {
    tabs.push({
      id: 'm365-report',
      label: 'M365 Report',
      path: `${basePath}/m365-report`,
      iconPath: ICON_PATHS.m365
    })
  }

  // Retainer-only tabs
  if (isRetainer) {
    if (portalSettings.showPlan !== false) {
      tabs.push({
        id: 'plan',
        label: 'Enablement Plan',
        path: `${basePath}/plan`,
        iconPath: ICON_PATHS.plan,
        comingSoon: true
      })
    }

    if (portalSettings.showPolicies) {
      tabs.push({
        id: 'policies',
        label: 'Policies',
        path: `${basePath}/policies`,
        iconPath: ICON_PATHS.policies
      })
    }

    if (portalSettings.showSecurity !== false) {
      tabs.push({
        id: 'security',
        label: 'Security Profile',
        path: `${basePath}/security`,
        iconPath: ICON_PATHS.security
      })
    }
  }

  // Document Vault — when enabled
  if (portalSettings.showVault || portalSettings.showDocuments) {
    tabs.push({
      id: 'vault',
      label: 'Document Vault',
      path: `${basePath}/vault`,
      iconPath: ICON_PATHS.vault
    })
  }

  // Activity Log — always visible
  tabs.push({
    id: 'activity',
    label: 'Activity Log',
    path: `${basePath}/activity`,
    iconPath: ICON_PATHS.activity
  })

  // Determine active tab from current path
  const activeTab = tabs.find(t => t.path !== basePath && location.pathname.startsWith(t.path))
    || tabs.find(t => t.path === basePath && (location.pathname === basePath || location.pathname === basePath + '/'))
    || tabs[0]

  return { tabs, activeTab, basePath, isRetainer, client }
}
